import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Menu, X, Globe } from 'lucide-react'
import useModalStore from '@/stores/modalStore'
import useLanguageStore, { Locale } from '@/stores/languageStore'
import { getSiteSettings } from '@/services/content'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { openModal } = useModalStore()
  const { currentLocale, setLocale } = useLanguageStore()
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [isLoadingSettings, setIsLoadingSettings] = useState(true)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoadingSettings(true)
      const { data } = await getSiteSettings(currentLocale)
      if (data?.company_logo_url) {
        setLogoUrl(data.company_logo_url)
      } else {
        setLogoUrl(null)
      }
      setIsLoadingSettings(false)
    }
    fetchSettings()
  }, [currentLocale])

  const navLinks = [
    {
      name: currentLocale === 'pt' ? 'O Problema' : 'The Problem',
      href: '#problem',
    },
    {
      name: currentLocale === 'pt' ? 'Solução' : 'Solution',
      href: '#solution',
    },
    {
      name: currentLocale === 'pt' ? 'Como Funciona' : 'How it works',
      href: '#how-it-works',
    },
    {
      name: currentLocale === 'pt' ? 'Para Quem' : 'For Whom',
      href: '#target',
    },
  ]

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    e.preventDefault()
    setMobileMenuOpen(false)
    const element = document.querySelector(href)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const handleLanguageChange = (locale: Locale) => {
    setLocale(locale)
    setMobileMenuOpen(false)
  }

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-transparent',
        isScrolled
          ? 'bg-white/80 backdrop-blur-md shadow-sm border-slate-100 py-3'
          : 'bg-transparent py-5',
      )}
    >
      <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
        <div className="flex items-center">
          <Link
            to="/"
            className="flex items-center gap-2 outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md"
          >
            {/* Dynamic Logo with Text Fallback */}
            {!isLoadingSettings && logoUrl ? (
              <img
                src={logoUrl}
                alt="Núcleo Digital"
                className="h-8 md:h-10 w-auto object-contain"
              />
            ) : (
              <span className="text-2xl font-bold tracking-tight text-slate-900">
                Núcleo Digital
              </span>
            )}
          </Link>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="text-sm font-medium text-slate-600 hover:text-primary transition-colors"
            >
              {link.name}
            </a>
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2 px-2">
                <Globe className="h-4 w-4" />
                <span className="uppercase">{currentLocale}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleLanguageChange('pt')}>
                Português (PT)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleLanguageChange('en')}>
                English (EN)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button onClick={openModal} className="font-semibold shadow-md">
            {currentLocale === 'pt' ? 'Pedir demonstração' : 'Request Demo'}
          </Button>
        </nav>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center gap-4">
          <button
            className="p-2 text-slate-600"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-white border-b border-slate-100 shadow-lg p-4 flex flex-col gap-4 animate-in slide-in-from-top-5">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="text-base font-medium text-slate-600 py-2 border-b border-slate-50 last:border-0"
            >
              {link.name}
            </a>
          ))}

          <div className="flex items-center justify-center gap-4 py-2 border-b border-slate-50">
            <Button
              variant={currentLocale === 'pt' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => handleLanguageChange('pt')}
            >
              PT
            </Button>
            <Button
              variant={currentLocale === 'en' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => handleLanguageChange('en')}
            >
              EN
            </Button>
          </div>

          <Button onClick={openModal} className="w-full mt-2">
            {currentLocale === 'pt' ? 'Pedir demonstração' : 'Request Demo'}
          </Button>
        </div>
      )}
    </header>
  )
}
